import { useEffect, useRef } from "react"

import { useTranslation } from "react-i18next"
import { Outlet } from "react-router-dom"

import { ArrowLeft, ArrowRight } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { useSidebar } from "@/components/ui/use-sidebar"
import { useScrollPosition } from "@/features/training/hooks/use-scroll-position"
import { useTrainingNavigation } from "@/features/training/hooks/use-training-navigation"
import { cn } from "@/lib/utils"

export function TrainingLayout() {
  const { t } = useTranslation("", { keyPrefix: "training.interface" })
  const { state: sidebarState, isMobile, setOpen } = useSidebar()
  const articleRef = useRef<HTMLElement | null>(null)
  const { isAtBottom } = useScrollPosition(articleRef)
  const {
    isOnCoursePage,
    handleNextClick,
    handleBackClick,
    canGoPrevious,
    updateModuleStatusMutation,
    isCurrentModuleCompleted,
    isOnLastPage,
    isOnQuizPage,
  } = useTrainingNavigation()

  const isExpanded = sidebarState === "expanded" && !isMobile

  // Hide sidebar on landing page, show on course pages
  useEffect(() => {
    setOpen(isOnCoursePage)
  }, [isOnCoursePage, setOpen])

  return (
    <section className="h-[calc(100vh-64px)] p-2 sm:p-3">
      {/*
        `relative` establishes the containing block for absolutely positioned
        descendants. Without it, Tailwind's `sr-only` (position: absolute) has
        no positioned ancestor anywhere in this chain, so it anchors to the
        initial containing block, escapes this scroller's clipping, and sits at
        its static offset — stretching the app-level scroll container and
        producing a second scrollbar that scrolls far past the content.
      */}
      <article
        ref={articleRef}
        className="relative h-full max-h-[calc(100vh-140px)] overflow-auto px-1 sm:px-0"
      >
        <article className="mx-auto h-full max-w-4xl">
          <Outlet />
        </article>
      </article>
      {isOnCoursePage && !isOnQuizPage && (
        <>
          <Button
            disabled={!canGoPrevious || updateModuleStatusMutation.isPending}
            className={cn(
              "fixed bottom-2 flex items-center text-sm sm:bottom-4 sm:text-base",
              isExpanded
                ? "left-[calc(var(--sidebar-width)+0.5rem)] sm:left-[calc(var(--sidebar-width)+1rem)]"
                : "left-[calc(var(--sidebar-width-icon)+0.5rem)] sm:left-[calc(var(--sidebar-width-icon)+1rem)]"
            )}
            onClick={handleBackClick}
          >
            <ArrowLeft className="size-4" />
            {t("buttons.back")}
          </Button>
          <Button
            className="fixed right-2 bottom-2 flex items-center text-sm sm:right-4 sm:bottom-4 sm:text-base"
            disabled={
              isCurrentModuleCompleted ? false : !isAtBottom || updateModuleStatusMutation.isPending
            }
            onClick={handleNextClick}
          >
            {isOnLastPage ? t("buttons.finish") : t("buttons.next")}
            <ArrowRight className="size-4" />
          </Button>
        </>
      )}
    </section>
  )
}
